import { Box, Container, Typography, Paper, Grid, Divider, Button, Chip, Stack, List, ListItem, ListItemIcon, ListItemText, Link } from '@mui/material';
import {
  Work,
  School,
  Code,
  Language,
  EmojiEvents,
  Assignment,
  ArrowBack,
  Download,
  CheckCircle
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

function Resume() {
  const resumeRef = useRef(null);

  const handleDownloadPDF = async () => {
    const resumeElement = resumeRef.current;
    if (!resumeElement) return;

    try {
      // Show loading state or notification here if needed

      const canvas = await html2canvas(resumeElement, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/jpeg', 1.0);

      // Calculate PDF dimensions based on canvas
      const pdfWidth = 210; // A4 width in mm
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);

      // Download the PDF
      pdf.save('NR7_Resume.pdf');

    } catch (error) {
      console.error('Error generating PDF:', error);
      // Show error notification here if needed
    }
  };
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }} ref={resumeRef}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Button
            component={RouterLink}
            to="/founder"
            startIcon={<ArrowBack />}
            variant="outlined"
          >
            Back to Founder
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownloadPDF}
          >
            Download PDF
          </Button>
        </Box>

        <Typography variant="h2" gutterBottom textAlign="center" sx={{ mb: 6, fontWeight: 'bold' }}>
          Nikhilesh Rao(NR7)
        </Typography>
        <Typography variant="h5" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
          Aspiring Tech Entrepreneur | Software Developer | Founder & CEO, TIXO - The IX Organisation
        </Typography>

        {/* Contact Information */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="body1">
            Email: <Link href="mailto:nr7isgoat@proton.me" color="primary">nr7isgoat@proton.me</Link> | Location: India
          </Typography>
          <Typography variant="body1">
            Instagram: <Link href="https://instagram.com/thisisnr7" target="_blank" color="primary">@thisisnr7</Link> |
            Website: <Link href="https://ix-study.netlify.app" target="_blank" color="primary">ix-study.netlify.app</Link>
          </Typography>
        </Box>

        <Divider sx={{ mb: 6 }} />

        {/* Professional Summary */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
            Professional Summary
          </Typography>
          <Typography paragraph sx={{ fontSize: '1.1rem' }}>
            Motivated and entrepreneurial 9th grade student with a strong passion for technology, product building, and football. Founder of The IX Organization (TIXO), with early-stage development of IX-Study—an AI-powered study platform. Experienced in frontend and backend development using JavaScript, Node.js, and React. Balanced with a competitive sports mindset. Skilled at taking initiative, building from scratch, and collaborating on vision-driven projects.
          </Typography>
        </Box>

        {/* Project Experience */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <Work sx={{ mr: 1 }} /> Project Experience
          </Typography>

          <Box sx={{ mb: 4 }}>
            <Grid container>
              <Grid item xs={12} md={8}>
                <Typography variant="h5" gutterBottom>
                IX-Study
                </Typography>
                <Typography variant="h6" gutterBottom>
                  Founder & Developer | TIXO
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Typography variant="body1" color="text.secondary">
                  2025 - Present
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  India
                </Typography>
              </Grid>
            </Grid>
            <Typography paragraph sx={{ mt: 2 }}>
              • Developed an AI-powered study assistant offering scheduling, flashcards, and quiz modes.
            </Typography>
            <Typography paragraph>
              • Planned monetization model including freemium tiers and referral systems.
            </Typography>
            <Typography paragraph>
              • Implemented locked-in pricing structure and future roadmap for features.
            </Typography>
            <Typography paragraph>
              • Currently integrating habit tracking, AI-generated notes, and chat-based study guides.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Grid container>
              <Grid item xs={12} md={8}>
                <Typography variant="h5" gutterBottom>
                  The IX Organization (TIXO)
                </Typography>
                <Typography variant="h6" gutterBottom>
                  Founder
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Typography variant="body1" color="text.secondary">
                  2025 - Present
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  India
                </Typography>
              </Grid>
            </Grid>
            <Typography paragraph sx={{ mt: 2 }}>
              • Established umbrella brand for future educational and utility-based apps.
            </Typography>
            <Typography paragraph>
              • Leading strategic development, brand identity, and team collaboration with peers.
            </Typography>
          </Box>
        </Box>



        {/* Skills */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <Code sx={{ mr: 1 }} /> Key Skills
          </Typography>

          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Programming
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                <Chip label="JavaScript" color="primary" />
                <Chip label="Node.js" color="primary" />
                <Chip label="React.js" color="primary" />
                <Chip label="Tailwind" color="primary" />
                <Chip label="MUI" color="primary" />
                <Chip label="Using AI" color="primary" />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Tools & Platforms
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                <Chip label="GitHub" color="secondary" />
                <Chip label="Netlify" color="secondary" />
                <Chip label="Claude" color="secondary" />
                <Chip label="advanced AI API" color="secondary" />
                <Chip label="Together AI" color="secondary" />
              </Stack>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Product Building
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                <Chip label="MVP development" color="primary" />
                <Chip label="Feature planning" color="primary" />
                <Chip label="Freemium model design" color="primary" />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Marketing
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                <Chip label="Brand ideation" color="secondary" />
                <Chip label="Referral systems" color="secondary" />
                <Chip label="User-centric design thinking" color="secondary" />
              </Stack>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Soft Skills
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                <Chip label="Leadership" color="primary" />
                <Chip label="Adaptability" color="primary" />
                <Chip label="Strategic thinking" color="primary" />
                <Chip label="Collaboration" color="primary" />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Interests */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <Assignment sx={{ mr: 1 }} /> Interests
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Startup culture and tech innovation"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="AI tools, productivity apps, and human-centered design"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Football strategy and performance"
              />
            </ListItem>
          </List>
        </Box>

        {/* Online */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <Language sx={{ mr: 1 }} /> Online
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Portfolio/GitHub"
                secondary={
                  <Link href="https://github.com/officialTIXO" target="_blank" color="primary">
                    https://github.com/officialTIXO
                  </Link>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="TIXO website"
                secondary={
                  <Link href="https://ix-study.netlify.app" target="_blank" color="primary">
                    ix-study.netlify.app
                  </Link>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Instagram"
                secondary={
                  <Link href="https://instagram.com/thisisnr7" target="_blank" color="primary">
                    @thisisnr7
                  </Link>
                }
              />
            </ListItem>
          </List>
        </Box>



        {/* IX-Study Vision */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            IX-Study Vision
          </Typography>

          <Paper elevation={2} sx={{ p: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <Typography variant="h6" gutterBottom>
              "My vision for IX-Study is to create an AI-powered study platform that helps students learn more efficiently and effectively."
            </Typography>
            <Typography variant="body1" sx={{ fontStyle: 'italic', mt: 2 }}>
              IX-Study represents the culmination of my passion for education and technology. By leveraging advanced AI models like advanced AI, I'm creating tools that adapt to each student's unique learning style, helping them achieve their full potential more efficiently than ever before. As an 9th grade student myself, I understand the challenges students face and am building solutions I wish I had.
            </Typography>
          </Paper>
        </Box>


      </Paper>
    </Container>
  );
}

export default Resume;
